/*
 * Copyright 2026 Sony Corporation
 */

import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { Scene } from '@babylonjs/core/scene';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractEngine } from '@babylonjs/core/Engines/abstractEngine';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';

import type { Bounds, DefaultView } from './types';
import type { SavedView } from './view-memory';
import { attachExploreControls } from './explore-controls';
import { EDITOR_LAYER } from './layers';

// Required for ShadowGenerator to register its scene component.
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
// Installs Scene.pick. Without it every pick silently misses, so clicks never
// select and gizmo drags never grab.
import '@babylonjs/core/Culling/ray';

/**
 * Everything the editor owns, created once and outliving every scene switch.
 *
 * The Babylon `Scene` is in this list deliberately: switching scenery must not
 * mean rebuilding the graph, because the camera rigs, the gizmo and the preview
 * view all live in it (see scene-switch.md §1).
 */
export interface SceneEditor {
  readonly engine: AbstractEngine;
  readonly scene: Scene;
  readonly camera: ArcRotateCamera;
  readonly shadowGenerator: ShadowGenerator;
  /** Recomputes camera limits and clipping from the active scene's extent. */
  frame: (bounds: Bounds, view: DefaultView) => void;
  /**
   * The editor's own key light, which exists so a scene that brings none is
   * still visible. A document that supplies its own turns it off rather than
   * being lit twice.
   */
  setKeyLight: (enabled: boolean) => void;
  /** The current viewpoint, for remembering where the user was looking from. */
  readView: () => SavedView;
  /** Puts the camera back on a remembered viewpoint, within the framed limits. */
  applyView: (view: SavedView) => void;
  /** The extent last framed; camera rigs and gizmo handles size themselves from it. */
  activeBounds: () => Bounds;
  /** Points the editor's shadow map at the given casters, or clears it. */
  setShadowCasters: (casters: readonly AbstractMesh[] | undefined) => void;
  /** What the wheel measures the way ahead with, for surfaces the scene's picking misses; none restores it. */
  setSurfacePicker: (picker: ((x: number, y: number) => Vector3 | undefined) | undefined) => void;
  dispose: () => void;
}

export interface SceneEditorOptions {
  engine: AbstractEngine;
  /** Absent under NullEngine, where there is no element to attach input to. */
  canvas?: HTMLCanvasElement;
}

const SHADOW_MAP_SIZE = 1024;

/**
 * How wide the key light is, as a fraction of the shadow map.
 *
 * This is what makes a contact-hardening shadow read as real: the penumbra
 * grows with distance from the contact point, so a chair leg is sharp where it
 * meets the floor and soft at the seat. Too large and everything is mush; this
 * is about a domestic window's worth of softness.
 */
const LIGHT_SIZE_UV_RATIO = 0.03;

export const createSceneEditor = ({ engine, canvas }: SceneEditorOptions): SceneEditor => {
  const scene = new Scene(engine);
  // VS Code's editor background, so the viewport reads as part of the shell
  // rather than a panel sitting on top of it.
  scene.clearColor = new Color4(0.118, 0.118, 0.118, 1);

  const camera = new ArcRotateCamera('EditorCamera', -Math.PI / 2, 1.1, 10, Vector3.Zero(), scene);
  camera.layerMask = EDITOR_LAYER;

  // Until a scene commits there is nothing to size against; one unit is a
  // harmless stand-in that no rendered frame ever sees.
  let active: Bounds = { center: Vector3.Zero(), radius: 1 };
  let surfacePicker: ((x: number, y: number) => Vector3 | undefined) | undefined;
  const detachControls = canvas
    ? attachExploreControls(
        camera,
        canvas,
        scene,
        () => active,
        () => surfacePicker
      )
    : undefined;

  // Low, because an HDRI is the real ambient: image-based lighting already
  // arrives from every direction, and a hemispheric light on top of it flattens
  // exactly the shading that makes the IBL worth having. This is the floor for
  // a scene with no environment yet.
  const ambient = new HemisphericLight('EditorAmbient', new Vector3(0, 1, 0), scene);
  ambient.intensity = 0.25;

  const key = new DirectionalLight('EditorKey', new Vector3(-0.5, -1, -0.4), scene);
  key.intensity = 1.6;

  // One generator for the editor's lifetime. A per-scene generator would leak a
  // shadow map on every switch; only its membership is scene-supplied.
  const shadowGenerator = new ShadowGenerator(SHADOW_MAP_SIZE, key);
  // PCSS rather than plain PCF: the penumbra widens with distance from the
  // contact, which is the cue that tells an eye an object is standing on the
  // floor rather than floating above it.
  shadowGenerator.useContactHardeningShadow = true;
  shadowGenerator.contactHardeningLightSizeUVRatio = LIGHT_SIZE_UV_RATIO;

  const frame = (bounds: Bounds, view: DefaultView) => {
    // Every distance is derived from the scene's own extent, so a 0.3 m tabletop
    // and a 40 m stage both frame correctly with no constant in this file.
    const radius = Math.max(bounds.radius, Number.EPSILON);
    active = { center: bounds.center.clone(), radius };

    camera.setTarget(bounds.center.clone());
    camera.alpha = view.alpha;
    camera.beta = view.beta;
    camera.radius = view.radius;
    camera.lowerRadiusLimit = radius * 0.05;
    camera.upperRadiusLimit = radius * 20;
    camera.minZ = radius * 0.01;
    camera.maxZ = radius * 200;
    camera.panningSensibility = 1000 / radius;

    key.position = bounds.center.add(new Vector3(radius, radius * 2, radius));
    key.shadowMinZ = radius * 0.1;
    key.shadowMaxZ = radius * 6;
  };

  const setShadowCasters = (casters: readonly AbstractMesh[] | undefined) => {
    const shadowMap = shadowGenerator.getShadowMap();
    if (shadowMap) {
      shadowMap.renderList = [...(casters ?? [])];
    }
  };

  return {
    engine,
    scene,
    camera,
    shadowGenerator,
    frame,
    setKeyLight: (enabled) => key.setEnabled(enabled),

    readView: () => ({
      alpha: camera.alpha,
      beta: camera.beta,
      radius: camera.radius,
      target: { x: camera.target.x, y: camera.target.y, z: camera.target.z },
    }),

    applyView: ({ alpha, beta, radius, target }) => {
      camera.setTarget(new Vector3(target.x, target.y, target.z));
      camera.alpha = alpha;
      camera.beta = beta;
      // Clamped here rather than left to the camera's own check, which only runs
      // on the next input and would otherwise save an out-of-range radius back.
      camera.radius = Math.min(Math.max(radius, camera.lowerRadiusLimit ?? radius), camera.upperRadiusLimit ?? radius);
    },

    activeBounds: () => active,
    setShadowCasters,
    setSurfacePicker: (picker) => {
      surfacePicker = picker;
    },
    dispose: () => {
      detachControls?.();
      shadowGenerator.dispose();
      scene.dispose();
    },
  };
};
